/**
 * Pure helpers for friend leagues: invite codes, name rules and the league week.
 * The same rules are enforced again on the server (supabase/schema.sql); these
 * only give instant feedback and consistent formatting.
 */

import { formatNumber } from '../utils/format.js';

export const INVITE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
export const NICKNAME_LENGTH = { min: 2, max: 24 };
export const LEAGUE_NAME_LENGTH = { min: 2, max: 40 };
/** League weeks run Monday to Sunday in India time (see league_standings in schema.sql). */
export const LEAGUE_TIME_ZONE = 'Asia/Kolkata';

// Fixed abbreviations: locale formatting varies between browsers ("Sep" vs "Sept").
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const collapse = (text) => String(text ?? '').trim().replace(/\s+/g, ' ');

/** "qm7k-4rtx " -> "QM7K4RTX"; anything that cannot be a code comes back as ''. */
export function normalizeInviteCode(input) {
  const code = String(input ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  return code.length === 8 && [...code].every((c) => INVITE_ALPHABET.includes(c)) ? code : '';
}

/** "QM7K4RTX" -> "QM7K-4RTX", easier to read aloud and type. */
export function formatInviteCode(code) {
  return code && code.length === 8 ? `${code.slice(0, 4)}-${code.slice(4)}` : code || '';
}

export function inviteLink(code, { origin = window.location.origin, base = import.meta.env.BASE_URL } = {}) {
  return `${origin}${base}#/leagues?join=${code}`;
}

function checkLength(value, { min, max }, label) {
  const clean = collapse(value);
  if (clean.length < min || clean.length > max) return { clean, error: `${label} must be ${min} to ${max} characters.` };
  return { clean, error: null };
}

export const checkNickname = (value) => checkLength(value, NICKNAME_LENGTH, 'Nickname');
export const checkLeagueName = (value) => checkLength(value, LEAGUE_NAME_LENGTH, 'League name');

/** Calendar date (y, m, d) in the league time zone. */
function leagueDate(now) {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: LEAGUE_TIME_ZONE, year: 'numeric', month: '2-digit', day: '2-digit' })
    .formatToParts(now)
    .reduce((acc, p) => ({ ...acc, [p.type]: Number(p.value) }), {});
  return new Date(Date.UTC(parts.year, parts.month - 1, parts.day));
}

/** The current league week: its Monday and Sunday (as UTC dates) and whole days left including today. */
export function leagueWeek(now = new Date()) {
  const today = leagueDate(now);
  const offset = (today.getUTCDay() + 6) % 7; // Monday = 0
  const start = new Date(today.getTime() - offset * 86400000);
  const end = new Date(start.getTime() + 6 * 86400000);
  const fmt = (d) => `${DAYS[d.getUTCDay()]} ${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`;
  return { start, end, daysLeft: 7 - offset, label: `${fmt(start)} – ${fmt(end)}` };
}

/** Standard competition ranking (1, 2, 2, 4) over rows already sorted by weekly XP. */
export function rankStandings(rows) {
  let lastXp = null;
  let lastRank = 0;
  return rows.map((row, index) => {
    const rank = row.weekly_xp === lastXp ? lastRank : index + 1;
    lastXp = row.weekly_xp;
    lastRank = rank;
    return { ...row, rank };
  });
}

/** 1 -> "1st", 2 -> "2nd", 11 -> "11th", 23 -> "23rd". */
export function ordinal(n) {
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${n}th`;
  return `${n}${{ 1: 'st', 2: 'nd', 3: 'rd' }[n % 10] || 'th'}`;
}

/**
 * Where the learner stands in one league, and the single most useful thing to
 * say about it. `rows` are standings sorted by weekly XP, one of them `is_me`.
 * Returns null if the learner is not in the rows.
 */
export function describeStanding(rows) {
  const ranked = rankStandings(rows);
  const me = ranked.find((row) => row.is_me);
  if (!me) return null;
  const others = ranked.filter((row) => !row.is_me);
  const base = { rank: me.rank, size: ranked.length, weeklyXp: me.weekly_xp };
  const xp = (n) => `${formatNumber(n)} XP`;

  if (!others.length) {
    return { ...base, message: 'You are the only member so far. Share the invite code to get some competition.' };
  }
  const top = ranked[0];
  if (top.weekly_xp === 0) return { ...base, message: 'Nobody has scored yet this week. Be the first.' };
  if (me.weekly_xp === 0) {
    return { ...base, message: `No XP yet this week. ${top.display_name} leads with ${xp(top.weekly_xp)}.` };
  }
  if (me.rank === 1) {
    const tied = others.filter((row) => row.weekly_xp === me.weekly_xp);
    if (tied.length) return { ...base, message: `Tied for 1st with ${tied.map((row) => row.display_name).join(' and ')}.` };
    const second = others[0];
    return { ...base, message: `You lead ${second.display_name} by ${xp(me.weekly_xp - second.weekly_xp)}.` };
  }
  // The nearest member with strictly more XP is the one to catch.
  const above = [...ranked].reverse().find((row) => row.weekly_xp > me.weekly_xp);
  return { ...base, message: `${xp(above.weekly_xp - me.weekly_xp)} to pass ${above.display_name}.` };
}
